import type {
  OpencodeTodoItem,
  OpencodeToolPart,
} from "schemas/generated/opencode";

import React, { useState } from "react";

import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { SquarePenIcon } from "@replo/design-system/icons/animated/SquarePenIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { Check, ChevronDown, Circle, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { formatDuration } from "replo-utils/lib/datetime";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

export type CoalescedTodos = {
  todos: OpencodeTodoItem[];
  latestPart: OpencodeToolPart;
};

const TodoStatusIcon = ({ status }: { status: OpencodeTodoItem["status"] }) => {
  return exhaustiveSwitch(
    { status },
    "status",
  )({
    completed: () => <Check size={12} className="text-default shrink-0" />,
    in_progress: () => (
      <Loader2 size={12} className="text-muted shrink-0 animate-spin" />
    ),
    cancelled: () => (
      <Circle size={12} className="text-muted shrink-0 opacity-50" />
    ),
    pending: () => <Circle size={12} className="text-muted shrink-0" />,
  });
};

export const TodoToolRow = ({
  coalescedTodos,
}: {
  coalescedTodos: CoalescedTodos;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { todos, latestPart } = coalescedTodos;

  const completedCount = todos.filter((t) => t.status === "completed").length;
  const totalCount = todos.length;

  const isLatestRunning =
    latestPart.state.status === "running" ||
    latestPart.state.status === "pending";

  const duration = exhaustiveSwitch(
    latestPart.state,
    "status",
  )({
    pending: () => null,
    running: () => null,
    completed: (s) => formatDuration(s.time.end - s.time.start),
    error: (s) => formatDuration(s.time.end - s.time.start),
  });

  return (
    <div className="flex flex-col w-full">
      <div
        className="flex items-center justify-between w-full min-h-8 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isLatestRunning ? (
            <SquarePenIcon size={14} loop />
          ) : (
            <CircleCheckIcon size={14} animateOnMount />
          )}
          <span className="text-sm text-default">
            Plan — {completedCount}/{totalCount} completed
          </span>
        </div>
        <div className="flex items-center gap-2">
          {duration && (
            <span className="text-xs text-muted whitespace-nowrap">
              {duration}
            </span>
          )}
          <ChevronDown
            size={14}
            className={twMerge(
              "transition-transform shrink-0",
              !isExpanded && "rotate-[-90deg]",
            )}
          />
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            className="flex flex-col overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {todos.map((todo) => (
              <div
                key={todo.id}
                className="flex items-center gap-2 min-h-7 pl-5"
              >
                <TodoStatusIcon status={todo.status} />
                <span
                  className={twMerge(
                    "text-sm text-muted",
                    todo.status === "cancelled" && "line-through",
                  )}
                >
                  {todo.content}
                </span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
